/**
 * Question Type Registry for Form Builder V2
 *
 * Defines all 13 supported question types with their:
 * - Type identifiers and metadata
 * - Default configurations
 * - Validation functions
 * - Icons and categories
 */

import {
  Type,
  AlignLeft,
  Mail,
  Phone,
  Hash,
  Calendar,
  Link,
  ChevronDown,
  CheckSquare,
  Star,
  Sliders,
  ToggleLeft,
  Upload,
  LucideIcon,
} from 'lucide-react';

// ============================================
// TYPE DEFINITIONS
// ============================================

export type QuestionType =
  | 'short_text'
  | 'long_text'
  | 'email'
  | 'phone'
  | 'number'
  | 'date'
  | 'url'
  | 'dropdown'
  | 'checkboxes'
  | 'rating'
  | 'opinion_scale'
  | 'yes_no'
  | 'file_upload';

export type QuestionCategory = 'text' | 'choice' | 'scale' | 'special';

export interface ValidationRules {
  required?: boolean;
  regex?: string;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  allowedExtensions?: string[]; // For file uploads
  maxFileSize?: number; // In bytes
}

export interface DeviceConfig {
  order: number;
  visible: boolean;
}

export interface UIConfig {
  placeholder?: string;
  description?: string;
  showLetterIndicators?: boolean; // For dropdown/checkboxes: show A, B, C labels
  layout?: 'horizontal' | 'vertical'; // For choice questions
  columns?: number; // For checkboxes grid
}

export interface AnimationConfig {
  type: 'slide' | 'fade' | 'scale';
  direction?: 'horizontal' | 'vertical';
}

export interface QuestionOption {
  id: string;
  label: string;
  value: string;
  emoji?: string; // Optional emoji for visual options
  targetQuestionId?: string; // For branching
}

export interface QuestionConfig {
  type: QuestionType;
  text: string;
  required: boolean;
  placeholder?: string;
  options?: QuestionOption[];
  validationRules?: ValidationRules;
  deviceConfig?: {
    mobile: DeviceConfig;
    desktop: DeviceConfig;
  };
  uiConfig?: UIConfig;
  animationConfig?: AnimationConfig;
  branchingRules?: BranchingRule[];
  visibleOnHighRating?: boolean;
  visibleOnLowRating?: boolean;
  // Quiz-specific fields
  correctAnswer?: string | string[];  // Correct answer(s) for quiz questions
  points?: number;                     // Points awarded for correct answer
  explanation?: string;                // Explanation shown after answering
}

export interface BranchingRule {
  conditionType: 'equals' | 'contains' | 'greater_than' | 'less_than' | 'between';
  conditionValue: string | number | [number, number];
  targetQuestionId: string | 'end' | 'google_redirect';
}

// ============================================
// VALIDATION FUNCTIONS
// ============================================

export type ValidationResult = string | null; // null = valid, string = error message

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/;
const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

export function validateRequired(value: unknown, required?: boolean): ValidationResult {
  if (!required) return null;

  if (value === undefined || value === null || value === '') {
    return 'This field is required';
  }

  if (Array.isArray(value) && value.length === 0) {
    return 'Please select at least one option';
  }

  return null;
}

export function validateEmail(value: string, rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(value, rules?.required);
  if (requiredError) return requiredError;

  if (!value) return null;

  if (!emailRegex.test(value)) {
    return 'Please enter a valid email address';
  }

  return null;
}

export function validatePhone(value: string, rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(value, rules?.required);
  if (requiredError) return requiredError;

  if (!value) return null;

  // Remove all non-digits for validation
  const digitsOnly = value.replace(/\D/g, '');
  if (digitsOnly.length < 9 || digitsOnly.length > 15) {
    return 'Please enter a valid phone number';
  }

  return null;
}

export function validateUrl(value: string, rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(value, rules?.required);
  if (requiredError) return requiredError;

  if (!value) return null;

  if (!urlRegex.test(value)) {
    return 'Please enter a valid URL';
  }

  return null;
}

export function validateNumber(value: string | number, rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(value, rules?.required);
  if (requiredError) return requiredError;

  if (value === '' || value === undefined) return null;

  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(num)) {
    return 'Please enter a valid number';
  }

  if (rules?.min !== undefined && num < rules.min) {
    return `Value must be at least ${rules.min}`;
  }

  if (rules?.max !== undefined && num > rules.max) {
    return `Value must be at most ${rules.max}`;
  }

  return null;
}

export function validateText(value: string, rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(value, rules?.required);
  if (requiredError) return requiredError;

  if (!value) return null;

  if (rules?.minLength && value.length < rules.minLength) {
    return `Please enter at least ${rules.minLength} characters`;
  }

  if (rules?.maxLength && value.length > rules.maxLength) {
    return `Please enter at most ${rules.maxLength} characters`;
  }

  if (rules?.regex) {
    try {
      const regex = new RegExp(rules.regex);
      if (!regex.test(value)) {
        return 'Please enter a valid value';
      }
    } catch {
      // Invalid regex, skip validation
    }
  }

  return null;
}

export function validateDate(value: string, rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(value, rules?.required);
  if (requiredError) return requiredError;

  if (!value) return null;

  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return 'Please enter a valid date';
  }

  return null;
}

export function validateSelection(value: string | string[], rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(value, rules?.required);
  if (requiredError) return requiredError;

  return null;
}

export function validateFile(file: File | null, rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(file, rules?.required);
  if (requiredError) return requiredError;

  if (!file) return null;

  if (rules?.maxFileSize && file.size > rules.maxFileSize) {
    const sizeMB = (rules.maxFileSize / (1024 * 1024)).toFixed(1);
    return `File must be smaller than ${sizeMB}MB`;
  }

  if (rules?.allowedExtensions && rules.allowedExtensions.length > 0) {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !rules.allowedExtensions.includes(ext)) {
      return `Allowed file types: ${rules.allowedExtensions.join(', ')}`;
    }
  }

  return null;
}

export function validateRating(value: number, rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(value, rules?.required);
  if (requiredError) return requiredError;

  if (value < 1 || value > 5) {
    return 'Please select a rating between 1 and 5';
  }

  return null;
}

export function validateOpinionScale(value: number, rules?: ValidationRules): ValidationResult {
  const requiredError = validateRequired(value, rules?.required);
  if (requiredError) return requiredError;

  const min = rules?.min ?? 1;
  const max = rules?.max ?? 10;

  if (value < min || value > max) {
    return `Please select a value between ${min} and ${max}`;
  }

  return null;
}

// ============================================
// QUESTION TYPE INFO
// ============================================

export interface QuestionTypeInfo {
  type: QuestionType;
  label: string;
  description: string;
  icon: LucideIcon;
  category: QuestionCategory;
  defaultConfig: Partial<QuestionConfig>;
  validator: (value: unknown, rules?: ValidationRules) => ValidationResult;
  supportsOptions: boolean;
  supportsBranching: boolean;
  autoAdvance: boolean; // Whether to auto-advance after selection
}

export const questionTypes: QuestionTypeInfo[] = [
  // TEXT INPUTS (5)
  {
    type: 'short_text',
    label: 'Short Text',
    description: 'Single line text input',
    icon: Type,
    category: 'text',
    defaultConfig: {
      placeholder: 'Type your answer here...',
      validationRules: { maxLength: 200 },
    },
    validator: validateText,
    supportsOptions: false,
    supportsBranching: false,
    autoAdvance: false,
  },
  {
    type: 'long_text',
    label: 'Long Text',
    description: 'Multi-line text area',
    icon: AlignLeft,
    category: 'text',
    defaultConfig: {
      placeholder: 'Type your answer here...',
      validationRules: { maxLength: 5000 },
    },
    validator: validateText,
    supportsOptions: false,
    supportsBranching: false,
    autoAdvance: false,
  },
  {
    type: 'email',
    label: 'Email',
    description: 'Email address input with validation',
    icon: Mail,
    category: 'text',
    defaultConfig: {
      placeholder: 'name@example.com',
    },
    validator: validateEmail,
    supportsOptions: false,
    supportsBranching: false,
    autoAdvance: false,
  },
  {
    type: 'phone',
    label: 'Phone',
    description: 'Phone number input',
    icon: Phone,
    category: 'text',
    defaultConfig: {
      placeholder: '+1 (555) 000-0000',
    },
    validator: validatePhone,
    supportsOptions: false,
    supportsBranching: false,
    autoAdvance: false,
  },
  {
    type: 'number',
    label: 'Number',
    description: 'Numeric input with range validation',
    icon: Hash,
    category: 'text',
    defaultConfig: {
      placeholder: '0',
      validationRules: { min: 0 },
    },
    validator: validateNumber,
    supportsOptions: false,
    supportsBranching: true,
    autoAdvance: false,
  },

  // CHOICE (4)
  {
    type: 'dropdown',
    label: 'Dropdown',
    description: 'Single selection from list',
    icon: ChevronDown,
    category: 'choice',
    defaultConfig: {
      options: [
        { id: '1', label: 'Option 1', value: 'option_1' },
        { id: '2', label: 'Option 2', value: 'option_2' },
      ],
      uiConfig: { showLetterIndicators: true },
    },
    validator: validateSelection,
    supportsOptions: true,
    supportsBranching: true,
    autoAdvance: true,
  },
  {
    type: 'checkboxes',
    label: 'Checkboxes',
    description: 'Multiple selection from list',
    icon: CheckSquare,
    category: 'choice',
    defaultConfig: {
      options: [
        { id: '1', label: 'Option 1', value: 'option_1' },
        { id: '2', label: 'Option 2', value: 'option_2' },
      ],
      uiConfig: { showLetterIndicators: true, layout: 'vertical' },
    },
    validator: validateSelection,
    supportsOptions: true,
    supportsBranching: false,
    autoAdvance: false,
  },
  {
    type: 'yes_no',
    label: 'Yes / No',
    description: 'Binary choice question',
    icon: ToggleLeft,
    category: 'choice',
    defaultConfig: {
      options: [
        { id: 'yes', label: 'Yes', value: 'yes' },
        { id: 'no', label: 'No', value: 'no' },
      ],
    },
    validator: validateSelection,
    supportsOptions: false, // Fixed options
    supportsBranching: true,
    autoAdvance: true,
  },
  {
    type: 'date',
    label: 'Date',
    description: 'Date picker',
    icon: Calendar,
    category: 'choice',
    defaultConfig: {},
    validator: validateDate,
    supportsOptions: false,
    supportsBranching: false,
    autoAdvance: false,
  },

  // SCALE (2)
  {
    type: 'rating',
    label: 'Star Rating',
    description: '1-5 star rating',
    icon: Star,
    category: 'scale',
    defaultConfig: {
      validationRules: { min: 1, max: 5 },
    },
    validator: validateRating,
    supportsOptions: false,
    supportsBranching: true,
    autoAdvance: true,
  },
  {
    type: 'opinion_scale',
    label: 'Opinion Scale',
    description: 'Numeric scale (e.g., 1-10)',
    icon: Sliders,
    category: 'scale',
    defaultConfig: {
      validationRules: { min: 1, max: 10 },
      uiConfig: {
        description: 'Select a number',
      },
    },
    validator: validateOpinionScale,
    supportsOptions: false,
    supportsBranching: true,
    autoAdvance: true,
  },

  // SPECIAL (2)
  {
    type: 'url',
    label: 'Website URL',
    description: 'URL input with validation',
    icon: Link,
    category: 'special',
    defaultConfig: {
      placeholder: 'https://',
    },
    validator: validateUrl,
    supportsOptions: false,
    supportsBranching: false,
    autoAdvance: false,
  },
  {
    type: 'file_upload',
    label: 'File Upload',
    description: 'File attachment',
    icon: Upload,
    category: 'special',
    defaultConfig: {
      validationRules: {
        maxFileSize: 10 * 1024 * 1024, // 10MB
        allowedExtensions: ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'],
      },
    },
    validator: validateFile as (value: unknown, rules?: ValidationRules) => ValidationResult,
    supportsOptions: false,
    supportsBranching: false,
    autoAdvance: false,
  },
];

// ============================================
// HELPER FUNCTIONS
// ============================================

export function getQuestionTypeInfo(type: QuestionType): QuestionTypeInfo | undefined {
  return questionTypes.find(qt => qt.type === type);
}

export function getQuestionTypesByCategory(category: QuestionCategory): QuestionTypeInfo[] {
  return questionTypes.filter(qt => qt.category === category);
}

export function validateQuestionValue(
  type: QuestionType,
  value: unknown,
  rules?: ValidationRules
): ValidationResult {
  const typeInfo = getQuestionTypeInfo(type);
  if (!typeInfo) return 'Unknown question type';
  return typeInfo.validator(value, rules);
}

export function createDefaultQuestion(type: QuestionType, order: number): QuestionConfig {
  const typeInfo = getQuestionTypeInfo(type);
  if (!typeInfo) {
    throw new Error(`Unknown question type: ${type}`);
  }

  return {
    type,
    text: `New ${typeInfo.label} Question`,
    required: false,
    ...typeInfo.defaultConfig,
    deviceConfig: {
      mobile: { order, visible: true },
      desktop: { order, visible: true },
    },
    visibleOnHighRating: true,
    visibleOnLowRating: true,
  };
}

export function getValidatorForType(type: QuestionType): QuestionTypeInfo['validator'] {
  const typeInfo = getQuestionTypeInfo(type);
  if (!typeInfo) {
    return () => null;
  }
  return typeInfo.validator;
}

// Legacy type mapping (v1 -> v2)
export function mapLegacyQuestionType(legacyType: string): QuestionType {
  const mapping: Record<string, QuestionType> = {
    'text': 'short_text',
    'textarea': 'long_text',
    'select': 'dropdown',
    'multiselect': 'checkboxes',
    'radio': 'dropdown',
    'checkbox': 'checkboxes',
    'star_rating': 'rating',
    'nps': 'opinion_scale',
    'email': 'email',
    'phone': 'phone',
    'number': 'number',
    'date': 'date',
    'url': 'url',
    'file': 'file_upload',
  };

  return mapping[legacyType] || 'short_text';
}

// Category labels
export const categoryLabels: Record<QuestionCategory, string> = {
  text: 'Text Inputs',
  choice: 'Choice',
  scale: 'Scale & Rating',
  special: 'Special',
};

// Category icons for the type selector
export const categoryIcons: Record<QuestionCategory, LucideIcon> = {
  text: Type,
  choice: CheckSquare,
  scale: Star,
  special: Upload,
};
